import { useState } from "react";
import {
  Button,
  DeleteModal,
  Modal,
  Table,
} from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";

import { useLocalization, translateOperationalModule } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@shared/contexts";
import { useAdminAccessQuery } from "@shared/hooks";

import {
  useCreateLicenseAssignmentMutation,
  useDeleteLicenseAssignmentMutation,
  type AdminLicenseAssignmentFilters,
  type ListAdminLicenseAssignmentsResponseItem,
} from "../../api/license-assignments";
import type {
  AdminLicenseAssignmentFilterData,
  AssignLicenseFormData,
} from "../../schemas";
import { AdminLicenseAssignmentsActionMenu } from "./AdminLicenseAssignmentsActionMenu";
import { AdminLicenseAssignmentsFilter } from "./AdminLicenseAssignmentsFilter";
import { AssignLicenseForm } from "./AssignLicenseForm";

interface AdminLicenseAssignmentsTabProps {
  items: ListAdminLicenseAssignmentsResponseItem[];
  isLoading?: boolean;
  onFilter: (filters: Partial<AdminLicenseAssignmentFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<AdminLicenseAssignmentFilterData>;
  paginatedMeta?: PaginationMeta;
}

const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const AdminLicenseAssignmentsTab = ({
  items,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: AdminLicenseAssignmentsTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const { hasAdminAccess } = useAdminAccessQuery();

  const [assignOpen, setAssignOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] =
    useState<ListAdminLicenseAssignmentsResponseItem | null>(null);

  // canWrite reflete a regra que o backend aplica (assertNotOwnerViewer +
  // EnsureTenantAdminAccess). Hoje usamos me.role (master) + hasAdminAccess
  // como proxy porque o front ainda não recebe o tenantRole canônico — ver
  // TODO em @shared/hooks/useAdminAccessQuery.ts. Apoia-se no espelhamento
  // 1:1 master↔tenant de owner/ownerViewer documentado em docs/permissions.md;
  // quando o backend expuser tenantRole, trocar por:
  //   canWrite = tenantRole !== 'ownerViewer' && hasAdminAccess
  const canWrite =
    me?.role !== "ownerViewer" && (me?.role === "owner" || hasAdminAccess);

  const createMutation = useCreateLicenseAssignmentMutation();
  const deleteMutation = useDeleteLicenseAssignmentMutation();

  const handleAssign = (data: AssignLicenseFormData) => {
    createMutation.mutate(data, {
      onSuccess: () => setAssignOpen(false),
    });
  };

  const handleRevoke = () => {
    if (!revokeTarget) return;
    deleteMutation.mutate(revokeTarget.assignmentId, {
      onSuccess: () => setRevokeTarget(null),
      onError: () => setRevokeTarget(null),
    });
  };

  const createActionMenu = (rowData: {
    rowData: ListAdminLicenseAssignmentsResponseItem;
  }) => {
    if (!canWrite) return null;
    return (
      <AdminLicenseAssignmentsActionMenu
        assignment={rowData.rowData}
        onRevoke={setRevokeTarget}
      />
    );
  };

  return (
    <div>
      <Table<ListAdminLicenseAssignmentsResponseItem>
        userId={me?.id ?? ""}
        tableId="admin-license-assignments-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={
          canWrite ? (createActionMenu as React.ComponentType) : undefined
        }
        TableButtons={
          canWrite ? (
            <Button
              size="small"
              variant="primary"
              onClick={() => setAssignOpen(true)}
              iconLeft={<PlusCircle />}
            >
              {translate("admin.licenseAssignments.button.assign")}
            </Button>
          ) : undefined
        }
        TableFilter={() => (
          <AdminLicenseAssignmentsFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate(
          "admin.licenseAssignments.table.filterButtonText",
        )}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "assigneeName",
            label: translate(
              "admin.licenseAssignments.table.column.assigneeName",
            ),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "assigneeEmail",
            label: translate(
              "admin.licenseAssignments.table.column.assigneeEmail",
            ),
            minWidth: "200px",
            sortType: "text",
          },
          {
            key: "complexName",
            label: translate("admin.licenseAssignments.table.column.complex"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "structureName",
            label: translate("admin.licenseAssignments.table.column.structure"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "moduleName",
            label: translate("admin.licenseAssignments.table.column.module"),
            minWidth: "120px",
            sortType: "text",
          },
          {
            key: "validity",
            label: translate("admin.licenseAssignments.table.column.validity"),
            minWidth: "180px",
          },
        ]}
        tableData={items.map(
          (item) =>
            ({
              ...item,
              moduleName: translateOperationalModule(item.moduleName, translate),
              validity: `${formatDate(item.startDate)} – ${formatDate(
                item.endDate,
              )}`,
            }) as { [key: string]: unknown; color?: string },
        )}
      />

      <Modal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={translate("admin.licenseAssignments.modal.assignTitle")}
        content={
          <AssignLicenseForm
            onSubmit={handleAssign}
            onCancel={() => setAssignOpen(false)}
            isSubmitting={createMutation.isPending}
          />
        }
      />

      <DeleteModal
        open={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        isDeleting={deleteMutation.isPending}
        itemDescription={
          revokeTarget
            ? `${revokeTarget.assigneeName} — ${translateOperationalModule(revokeTarget.moduleName, translate)}`
            : undefined
        }
        title={translate("admin.licenseAssignments.modal.revokeTitle")}
        message={translate("admin.licenseAssignments.modal.revokeMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("admin.licenseAssignments.button.revoke")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default AdminLicenseAssignmentsTab;
